// UsersTableBase.jsx
import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  InputBase,
  Select,
  MenuItem,
  Divider,
  IconButton,
  TablePagination,
  Button,
  Checkbox,
  Avatar,
  Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import {
  HAIRLINE,
  HAIRLINE_STRONG,
  ACCENT,
  STATUS,
  SHADOW_LG,
  BADGE_COLORS,
  withAlpha,
} from "../../../../theme/tokens.js";
import { getInitials, getFullName } from "../../../../utils/userDisplay.js";
import {
  dataTableHeadSx,
  SortableHeadCell,
} from "../../../../components/DataTableHeader.jsx";

function hashString(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0; // keep unsigned 32-bit
  }
  return hash;
}

// Deterministic color from the app's shared BADGE_COLORS palette (same one
// the Models page uses for pipeline-task badges) — a given group name always
// gets the same color, and that color means the same thing on every page.
function tagStyle(str) {
  const color = BADGE_COLORS[hashString(str || "") % BADGE_COLORS.length];
  return { bg: withAlpha(color, 0.12), fg: color };
}

// Role badges — "admin" gets a fixed, attention-drawing color from the same
// shared palette; every other role falls back to the palette's neutral.
const ROLE_STYLES = {
  admin: { bg: withAlpha(BADGE_COLORS[3], 0.12), fg: BADGE_COLORS[3] }, // rose
};
const ROLE_DEFAULT = {
  bg: withAlpha(BADGE_COLORS[4], 0.12),
  fg: BADGE_COLORS[4],
}; // slate

// Soft chip styling: tinted fill + solid-color text, no border — matches the
// pipeline/category badge treatment used on the Models page.
function badgeSx({ bg, fg }) {
  return {
    bgcolor: bg,
    color: fg,
    border: "1px solid transparent",
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 22,
    "& .MuiChip-label": { px: 1 },
  };
}

// Missing/invalid timestamps sort as oldest.
const createdTime = (user) => {
  const t = Date.parse(user.created_at);
  return Number.isNaN(t) ? 0 : t;
};

// Same format as the user details drawer.
const formatCreatedDate = (createdAt) => {
  if (!createdAt) return "-";
  const date = new Date(createdAt);
  return date.toLocaleDateString() + " " + date.toLocaleTimeString();
};

// Per-column comparators (ascending). User, Role, Status, Created are sortable.
const columnCompare = {
  user: (a, b) =>
    getFullName(a.first_name, a.last_name, a.email).localeCompare(
      getFullName(b.first_name, b.last_name, b.email),
    ),
  role: (a, b) =>
    (a.permission_level || "").localeCompare(b.permission_level || ""),
  status: (a, b) =>
    Number(b.is_currently_logged_in) - Number(a.is_currently_logged_in),
  created: (a, b) => createdTime(a) - createdTime(b),
};

const UsersTableBase = ({
  users = [],
  usersPerPage = 20,
  loading = false,
  error = null,
  mode = "view",
  selectedUserIds = [],
  membershipsByUserId = new Map(),
  onClickDetails,
  onSelectedUserIdsChange,
  onFilteredUsersChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(usersPerPage);
  const [searchBy, setSearchBy] = useState("email"); // 'email' or 'name'
  const [orderBy, setOrderBy] = useState("status");
  const [order, setOrder] = useState("asc"); // 'asc' | 'desc'

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(column);
      setOrder("asc");
    }
    setPage(0);
  };

  // Sortable header — shared styling lives in <SortableHeadCell> so every data
  // table across the app stays visually identical.
  const renderSortableHeader = (column, label) => (
    <SortableHeadCell
      label={label}
      active={orderBy === column}
      direction={orderBy === column ? order : "asc"}
      onClick={() => handleSort(column)}
    />
  );

  const enableSelection = mode === "select";
  const showActions = mode === "view" && !!onClickDetails;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { sortedUsers, pageUsers } = useMemo(() => {
    const term = searchTerm.toLowerCase();

    const filtered =
      users?.filter((user) => {
        if (!term) return true;
        if (searchBy === "email") {
          return user.email.toLowerCase().includes(term);
        }
        if (searchBy === "name") {
          const fullName = `${user.first_name || ""} ${
            user.last_name || ""
          }`.toLowerCase();
          return fullName.includes(term);
        }
        if (searchBy === "group") {
          return (membershipsByUserId.get(user.id) || []).some((p) =>
            p.name.toLowerCase().includes(term),
          );
        }
        return true;
      }) || [];

    const sorted = [...filtered].sort((a, b) => {
      const cmp = columnCompare[orderBy](a, b);
      return order === "desc" ? -cmp : cmp;
    });

    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    const pageSlice = sorted.slice(start, end);

    return {
      sortedUsers: sorted,
      pageUsers: pageSlice,
    };
  }, [
    users,
    membershipsByUserId,
    searchTerm,
    searchBy,
    page,
    rowsPerPage,
    orderBy,
    order,
  ]);

  useEffect(() => {
    if (onFilteredUsersChange) {
      onFilteredUsersChange(sortedUsers);
    }
  }, [sortedUsers, onFilteredUsersChange]);

  const allSelectedOnPage =
    enableSelection &&
    pageUsers.length > 0 &&
    pageUsers.every((u) => selectedUserIds?.includes(u.id));

  const handleToggleOne = (userId) => {
    if (!enableSelection || !onSelectedUserIdsChange) return;
    const isSelected = selectedUserIds.includes(userId);
    if (isSelected) {
      onSelectedUserIdsChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectedUserIdsChange([...selectedUserIds, userId]);
    }
  };

  const handleToggleAllOnPage = () => {
    if (!enableSelection || !onSelectedUserIdsChange) return;
    if (allSelectedOnPage) {
      const pageIds = new Set(pageUsers.map((u) => u.id));
      onSelectedUserIdsChange(selectedUserIds.filter((id) => !pageIds.has(id)));
    } else {
      const pageIds = pageUsers.map((u) => u.id);
      const set = new Set([...selectedUserIds, ...pageIds]);
      onSelectedUserIdsChange(Array.from(set));
    }
  };

  // Total columns (for empty-state colSpan)
  const columnCount = 1 + 4 + (enableSelection ? 1 : 0) + (showActions ? 1 : 0);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pb: 2,
          gap: 2,
          flexWrap: "nowrap",
          minWidth: 0,
        }}
      >
        {/* Unified scoped search bar: [search] [scope ▾] | input [clear] */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: 40,
            px: 1,
            flex: "1 1 auto",
            minWidth: 0,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 2,
            bgcolor: "#fff",
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            "&:focus-within": {
              borderColor: ACCENT,
              boxShadow: `0 0 0 3px ${ACCENT}22`,
            },
          }}
        >
          <SearchIcon
            fontSize="small"
            sx={{ color: "text.secondary", ml: 0.5, flexShrink: 0 }}
          />
          <Select
            value={searchBy}
            onChange={(e) => {
              setSearchBy(e.target.value);
              setPage(0);
            }}
            variant="standard"
            disableUnderline
            aria-label="Search field"
            MenuProps={{
              // Open below the control (not overlapping the table), aligned left
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              sx: {
                "& .MuiPaper-root": {
                  mt: 0.5,
                  borderRadius: 2,
                  boxShadow: SHADOW_LG,
                },
                // Match the menu font to the trigger text size
                "& .MuiMenuItem-root": {
                  fontSize: "0.8125rem",
                  minHeight: "auto",
                  py: 0.75,
                },
              },
            }}
            sx={{
              flexShrink: 0,
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.secondary",
              "& .MuiSelect-select": {
                py: 0,
                pl: 0.75,
                pr: "20px !important",
              },
            }}
          >
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="group">Group</MenuItem>
          </Select>
          <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.25 }} />
          <InputBase
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            placeholder={`Search by ${searchBy}…`}
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              fontSize: "0.875rem",
              "& input": { py: 0 },
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => {
                setSearchTerm("");
                setPage(0);
              }}
              sx={{ flexShrink: 0 }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, 50]}
          component="div"
          count={sortedUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{
            flexShrink: 0,
            ml: "auto",
            "& .MuiTablePagination-toolbar": { pl: 1 },
            "& .MuiTypography-root": { fontSize: "0.875rem" },
          }}
        />
      </Box>

      <TableContainer sx={{ pt: 0, overflowX: "auto" }}>
        <Table size="small" sx={dataTableHeadSx}>
          <TableHead>
            <TableRow>
              {enableSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelectedOnPage}
                    indeterminate={
                      !allSelectedOnPage &&
                      pageUsers.some((u) => selectedUserIds.includes(u.id))
                    }
                    onChange={handleToggleAllOnPage}
                  />
                </TableCell>
              )}
              {renderSortableHeader("user", "User")}
              {renderSortableHeader("role", "Role")}
              {renderSortableHeader("status", "Status")}
              <TableCell>Groups</TableCell>
              {renderSortableHeader("created", "Created")}
              {showActions && <TableCell align="right" />}
            </TableRow>
          </TableHead>

          <TableBody>
            {pageUsers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  No users found.
                </TableCell>
              </TableRow>
            )}

            {pageUsers.map((user) => {
              const isSelected =
                enableSelection && selectedUserIds.includes(user.id);
              const hasName = Boolean(
                (user.first_name || "").trim() || (user.last_name || "").trim(),
              );
              const name = getFullName(
                user.first_name,
                user.last_name,
                user.email,
              );
              const avatar = tagStyle(user.email || user.id || "");
              const active = user.is_currently_logged_in;
              const groups = membershipsByUserId.get(user.id) || [];
              const shownGroups = groups.slice(0, 5);
              const extraGroups = groups.length - shownGroups.length;

              return (
                <TableRow key={user.id} hover selected={isSelected}>
                  {enableSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleOne(user.id)}
                      />
                    </TableCell>
                  )}

                  {/* Identity: avatar + name + email */}
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          bgcolor: avatar.bg,
                          color: avatar.fg,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(
                          user.first_name,
                          user.last_name,
                          user.email,
                        )}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "text.primary",
                            lineHeight: 1.3,
                          }}
                        >
                          {name}
                        </Typography>
                        {hasName && (
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              color: "text.secondary",
                              lineHeight: 1.3,
                            }}
                          >
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Role */}
                  <TableCell>
                    <Chip
                      size="small"
                      label={user.permission_level}
                      sx={badgeSx(
                        ROLE_STYLES[user.permission_level] || ROLE_DEFAULT,
                      )}
                    />
                  </TableCell>

                  {/* Status: dot + label */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: active ? STATUS.success : HAIRLINE_STRONG,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          color: active ? "text.primary" : "text.secondary",
                        }}
                      >
                        {active ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Groups */}
                  <TableCell>
                    {groups.length === 0 ? (
                      <Typography
                        sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
                      >
                        —
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {shownGroups.map((p) => (
                          <Chip
                            key={p.id}
                            label={p.name}
                            size="small"
                            sx={badgeSx(tagStyle(p.name))}
                          />
                        ))}
                        {extraGroups > 0 && (
                          <Tooltip
                            title={groups
                              .slice(4)
                              .map((p) => p.name)
                              .join(", ")}
                          >
                            <Chip
                              label={`+${extraGroups}`}
                              size="small"
                              sx={badgeSx(ROLE_DEFAULT)}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  {/* Creation time */}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography
                      sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
                    >
                      {formatCreatedDate(user.created_at)}
                    </Typography>
                  </TableCell>

                  {showActions && (
                    <TableCell align="right">
                      <Button
                        variant="text"
                        size="small"
                        endIcon={<ChevronRightIcon />}
                        onClick={() => onClickDetails?.(user)}
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          "& .MuiButton-endIcon": { ml: 0.25 },
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default UsersTableBase;
